import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    ThemeProvider,
    useMediaQuery
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { darkTheme } from '../../../theme/darkTheme';
import { useAuth } from '../../context/AuthContext';
import NavBar from '../../layout/NavBar';
import { TrainingApi, TestApi } from '../../../services/api';
import NavBarBot from '../../layout/NavBarBot';
import Notification from '../../util/notifications/Notification';
import BackButton from '../../util/buttons/BackButton';
import HeaderCard from '../../layout/HeaderCard';
import TrainingsplanCard from './components/customtrainingsplan/TrainingsplanCard';
import PlanUebungCard from './components/customtrainingsplan/PlanUebungCard';
import PlanDialog from './components/customtrainingsplan/PlanDialog';

// ==================== Main Component ====================
function CustomTrainingsplanManager() {

    const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
    const { nutzer } = useAuth();
    const [, setShowNotification] = useState(false);
    const [trainingsplaene, setTrainingsplaene] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [uebungenInPlan, setUebungenInPlan] = useState([]);
    const [alleUebungen, setAlleUebungen] = useState([]);
    const [customUebungen, setCustomUebungen] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState('createPlan');
    const [editingItem, setEditingItem] = useState(null);
    const [filterKategorie, setFilterKategorie] = useState('');
    const [filterZielmuskel, setFilterZielmuskel] = useState('');
    const [selectedUebung, setSelectedUebung] = useState(null);


    const [formData, setFormData] = useState({
        name: '',
        beschreibung: '',
        notizen: '',
        selectedUebung: null,
    });

    const sensors = useSensors(
        useSensor(TouchSensor, { activationConstraint: { delay: 0, tolerance: 5 } }),
        useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );
    const NUTZER_ID = nutzer?.id;

    useEffect(() => {
        if (NUTZER_ID) {
            loadTrainingsplaene();
            loadVerfuegbareUebungen();
            loadCustomUebungen();
        }
    }, [NUTZER_ID]);

    const loadTrainingsplaene = async () => {
        try {
            const response = await TrainingApi.getCustomPlaene(NUTZER_ID);
            setTrainingsplaene(response.data);
            setMessage({ type: "", text: "" });
        } catch (err) {
            setMessage({ type: "error", text: 'Trainingspläne konnten nicht geladen werden' });
            console.error(err);
        }
    };

    const loadVerfuegbareUebungen = async () => {
        try {
            const response = await TestApi.getAllUebungen();
            setAlleUebungen(response.data);
        } catch (err) {
            console.error('Fehler beim Laden der Übungen:', err);
        }
    };

    const loadCustomUebungen = async () => {
        try {
            const response = await TrainingApi.getUebungenByUserId(NUTZER_ID);
            setCustomUebungen(response.data);
        } catch (err) {
            console.error('Fehler beim Laden eigener Übungen:', err);
        }
    };

    const loadPlanDetails = async (planId) => {
        setLoading(true);
        try {
            const response = await TrainingApi.getCustomPlanUebungen(planId, NUTZER_ID);
            setSelectedPlan(response.data.plan);
            setUebungenInPlan(response.data.uebungen);
            setMessage({ type: "success", text: "Übung hinzugefügt" });
        } catch (err) {
            setMessage({ type: "error", text: 'Plandetails konnten nicht geladen werden' });
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // === Handler für Plan und Übungen (create, edit, delete) ===
    const handleCreatePlan = () => { setDialogType('createPlan'); setFormData({ name: '', beschreibung: '' }); setEditingItem(null); setDialogOpen(true); };
    const handleEditPlan = () => { if (!selectedPlan) return; setDialogType('editPlan'); setFormData({ name: selectedPlan.name, beschreibung: selectedPlan.beschreibung }); setEditingItem(selectedPlan); setDialogOpen(true); };
    const handleDeletePlan = async () => {
        if (!selectedPlan || !window.confirm('Trainingsplan wirklich löschen?')) return;
        try {
            await TrainingApi.deleteCustomPlan(selectedPlan.id, { nutzer_id: NUTZER_ID });
            await loadTrainingsplaene();
            setSelectedPlan(null);
            setMessage({ type: "success", text: "Trainingsplan gelöscht" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } catch (err) {
            setMessage({ type: "error", text: err });
            console.error(err);
        }
    };
    const handleAddUebung = () => { setDialogType('addUebung'); setFormData({ name: '', notizen: '', selectedUebung: null }); setEditingItem(null); setDialogOpen(true); };
    const handleEditUebung = (uebung) => { setDialogType('editUebung'); setFormData({ empfohlene_saetze: uebung.empfohlene_saetze, notizen: uebung.notizen }); setEditingItem(uebung); setDialogOpen(true); };
    const handleDeleteUebung = async (uebungId) => {
        if (!window.confirm('Übung wirklich entfernen?')) return;
        try {
            await TrainingApi.deleteCustomPlanUebung(uebungId, { nutzer_id: NUTZER_ID });
            await loadPlanDetails(selectedPlan.id);
            setMessage({ type: "success", text: "Übung gelöscht" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        }
        catch (err) {
            setMessage({ type: "error", text: 'Übung konnte nicht gelöscht werden' });
            console.error(err);
        }
    };

    const handleDialogConfirm = async () => {
        try {
            if (dialogType === 'createPlan') {
                await TrainingApi.createCustomPlan({ nutzer_id: NUTZER_ID, name: formData.name, beschreibung: formData.beschreibung });
                await loadTrainingsplaene();
            } else if (dialogType === 'editPlan') {
                await TrainingApi.updateCustomPlan(selectedPlan.id, { nutzer_id: NUTZER_ID, name: formData.name, beschreibung: formData.beschreibung });
                await loadPlanDetails(selectedPlan.id);
            } else if (dialogType === 'addUebung') {
                // support options that include uebung_id for custom and standard entries
                const selected = formData.selectedUebung;
                const uebungId = selected?.uebung_id ?? (typeof selected?.id === 'string' ? parseInt(selected.id.split('-').pop(), 10) : selected?.id);
                await TrainingApi.addUebungToPlan({
                    eigener_trainingsplan_id: selectedPlan.id,
                    uebung_id: uebungId,
                    reihenfolge: uebungenInPlan.length + 1,
                    notizen: formData.notizen || null,
                    nutzer_id: NUTZER_ID,
                    eigene_uebung: selected?.eigene_uebung ? 1 : 0,
                });
                await loadPlanDetails(selectedPlan.id);
            } else if (dialogType === 'editUebung') {
                await TrainingApi.updateUebungInPlan(editingItem.id, {
                    reihenfolge: editingItem.reihenfolge,
                    notizen: formData.notizen || null,
                    nutzer_id: NUTZER_ID,
                });
                await loadPlanDetails(selectedPlan.id);
            }

            setMessage({ type: "success", text: "Hat geklpatt" });
            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            setDialogOpen(false);
        } catch (err) {
            setMessage({ type: "error", text: err.response?.data?.error || err.message || 'Ein Fehler ist aufgetreten' });
            console.error('Error in handleDialogConfirm:', err);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (over && active.id !== over.id) {
            const oldIndex = uebungenInPlan.findIndex(u => u.id === active.id);
            const newIndex = uebungenInPlan.findIndex(u => u.id === over.id);
            const newOrder = arrayMove(uebungenInPlan, oldIndex, newIndex);
            setUebungenInPlan(newOrder);
            try {
                await Promise.all(newOrder.map((uebung, index) =>
                    TrainingApi.updateUebungInPlan(uebung.id, { ...uebung, reihenfolge: index + 1, nutzer_id: NUTZER_ID })
                ));
                setMessage({ type: "success", text: 'Reihenfolge gespeichert' });
                setTimeout(() => setMessage({ type: "", text: "" }), 3000);
            } catch (err) {
                setMessage({ type: "error", text: 'Fehler beim Speichern der Reihenfolge' });
                console.error(err);
                setUebungenInPlan(uebungenInPlan);
            }
        }
    };

    const closeSelectedPlan = () => {
        setSelectedPlan(null);
        // window.location.reload()
    }

    // Gefilterte Übungen für Autocomplete
    const gefilterteUebungen = alleUebungen
        .filter(u => !filterKategorie || u.kategorie === filterKategorie)
        .filter(u => !filterZielmuskel || u.zielmuskel === filterZielmuskel)
        .sort((a, b) => a.name.localeCompare(b.name));

    const gefilterteCustomUebungen = customUebungen
        .filter(u => !filterKategorie || u.kategorie === filterKategorie)
        .filter(u => !filterZielmuskel || u.zielmuskel === filterZielmuskel)
        .sort((a, b) => (a.uebung_name || '').localeCompare(b.uebung_name || ''));

    // Einzigartige Kategorien und Zielmuskeln für Filter-Dropdowns
    const kategorien = [...new Set(alleUebungen.map(u => u.kategorie))].sort();
    const zielmuskeln = [...new Set(alleUebungen.map(u => u.zielmuskel))].sort();

    if (!NUTZER_ID) {
        return (
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <Alert severity="error">Bitte melden Sie sich an</Alert>
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <>
            <NavBar />
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 8 }}>
                    <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
                        {!isMobile && (
                            selectedPlan ? (
                                <>
                                    <BackButton func={() => setSelectedPlan(null)} />
                                </>
                            ) : (
                                <BackButton />
                            )
                        )}
                        {!selectedPlan ? (
                            <>
                                <HeaderCard title={"Meine Trainingspläne"} />

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreatePlan}
                                    sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                                        color: '#fff',
                                        borderRadius: '16px',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #3b82f6 0%, #1e3a8a 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
                                        },
                                    }}
                                >
                                    Neuer Plan
                                </Button>

                                {message.status === "error" || message.status === "success" && (
                                    <Notification
                                        type={message.type}
                                        message={message.text}
                                        onClose={() => {
                                            setShowNotification(true);
                                            setMessage({ type: "", text: "" });
                                        }}
                                    />
                                )}

                                <Box>
                                    {trainingsplaene.length === 0 ? (
                                        <Card sx={{ mb: 2, borderRadius: "16px", background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                                            <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontSize: '1rem',
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        Noch keine Pläne erstellt
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ) : (
                                        trainingsplaene.map(plan => (
                                            <TrainingsplanCard
                                                key={plan.id}
                                                plan={plan}
                                                onSelect={() => loadPlanDetails(plan.id)}
                                            />
                                        ))
                                    )}
                                </Box>
                            </>
                        ) : (
                            <>

                                <HeaderCard title={selectedPlan.name} subtitle={selectedPlan.beschreibung} />

                                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-around" }}>
                                    <Button onClick={handleAddUebung} variant="outlined"><AddIcon /></Button>
                                    <Button onClick={handleEditPlan} variant="outlined" color="warning"><EditIcon /></Button>
                                    <Button onClick={handleDeletePlan} variant="outlined" color="error"><DeleteIcon /></Button>
                                </Box>

                                {message.status === "error" || message.status === "success" && (
                                    <Notification
                                        type={message.type}
                                        message={message.text}
                                        onClose={() => {
                                            setShowNotification(true);
                                            setMessage("");
                                        }}
                                    />
                                )}

                                {loading ? <CircularProgress sx={{ position: "absolute", top: "45%", left: "45%", display: 'block', mx: 'auto', mb: 2 }} />
                                    : uebungenInPlan.length === 0 ? (
                                        <Typography color="text.secondary">Keine Übungen im Plan</Typography>
                                    ) : (
                                        <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragStart={(event) => setActiveId(event.active.id)}
                                        onDragEnd={handleDragEnd}>
                                            <SortableContext
                                            items={uebungenInPlan.map(u => u.id)}
                                            strategy={verticalListSortingStrategy}>
                                                {uebungenInPlan.map((uebung) => (
                                                    <PlanUebungCard
                                                        key={uebung.id}
                                                        uebung={uebung}
                                                        onEdit={() => handleEditUebung(uebung)}
                                                        onDelete={() => handleDeleteUebung(uebung.id)}
                                                        isDragging={activeId === uebung.id}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    )}
                            </>
                        )}
                    </Container>

                    {/* ==================== Dialog ==================== */}
                    <PlanDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        onConfirm={handleDialogConfirm}
                        dialogType={dialogType}
                        formData={formData}
                        onFormData={setFormData}
                        filterKategorie={filterKategorie}
                        onFilterKategorie={setFilterKategorie}
                        filterZielmuskel={filterZielmuskel}
                        onFilterZielmuskel={setFilterZielmuskel}
                        kategorien={kategorien}
                        zielmuskeln={zielmuskeln}
                        gefilterteUebungen={gefilterteUebungen}
                        gefilterteCustomUebungen={gefilterteCustomUebungen}
                        selectedUebung={selectedUebung}
                        onSelectUebung={(newValue) => {
                            setSelectedUebung(newValue);
                            setFormData({ ...formData, selectedUebung: newValue });
                        }}
                    />
                </Box>
            </ThemeProvider >
            <NavBarBot
                // mainBtnF={handleAddUebung}
                // mainBtnTxt={<AddIcon />}
                sideBtn1F={selectedPlan && (closeSelectedPlan)}
                sideBtn1Icon={<ArrowBackIcon />} />
        </>
    );
}

export default CustomTrainingsplanManager;
